import { useQuery } from '@tanstack/react-query'
import type { OfferChannel } from '@today/types'
import type { ChannelMismatchWarningProps } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { OFFERS_NAMESPACE } from '@/i18n'
import { notificationTemplateService } from '@/services/notification-template.service'
import { channelMismatch } from './channel-mismatch'

/**
 * Step 4's channel-mismatch warning (TO-140 offer-notification-message REQ-109):
 * reads the linked template (only when the offer links one) and, while its channel
 * type is neither ALL nor among the selected channels, words the warning and holds
 * Send until "Send with this message anyway" is ticked. `reset` clears the tick —
 * the caller runs it on every channel change.
 */
export function useChannelMismatchWarning(
	offerId: string,
	templateId: string | null,
	selected: readonly OfferChannel[],
) {
	const { t } = useTranslation(OFFERS_NAMESPACE)
	const navigate = useNavigate()
	const [confirmed, setConfirmed] = useState(false)
	const template = useQuery({
		queryKey: ['offer-notification', 'template', templateId],
		queryFn: () => notificationTemplateService.get(templateId as string),
		enabled: templateId !== null,
		retry: false,
		refetchOnWindowFocus: false,
	})
	const channel = templateId !== null ? (template.data?.channelType ?? null) : null
	const shown = channelMismatch(channel, selected)
	const warning: ChannelMismatchWarningProps | null = shown
		? {
				message: t('send.messageMismatch.warning', { channel: t(`send.channels.${channel}`) }),
				confirmed,
				onConfirmedChange: setConfirmed,
				onChangeMessage: () => void navigate(`/offers/${encodeURIComponent(offerId)}/message`),
				labels: { confirm: t('send.messageMismatch.confirm'), changeMessage: t('send.messageMismatch.changeMessage') },
			}
		: null
	return { warning, blocksSend: shown && !confirmed, reset: () => setConfirmed(false) }
}
